import { FC, ReactNode } from "react";

import { AssessmentStatusChip } from "./AssessmentStatusChip";

import { AssessmentStatus } from "api/api-data-types";
import { DueDateButton } from "components/materials/page/DueDateButton";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { Tooltip } from "mds/components/Tooltip";
import { ChatIcon } from "mds/icons";
import { useCourseCapabilitiesByCourseId } from "store/capability-hooks";
import { useAppSelector } from "store/index";
import {
  selectAssessmentsByPageId,
  selectCourseUserByCourseId,
  selectPageAccessDataForCourseUser,
} from "store/selectors";
import { selectAssessmentInfoForPage } from "store/selectors/computed";

export type PageSubmissionStatusProps = {
  page: PageType;
  alwaysShowDueDates?: boolean;
  readOnly?: boolean;
};

export const PageSubmissionStatus: FC<PageSubmissionStatusProps> = ({
  page,
  readOnly = false,
  alwaysShowDueDates = false,
}) => {
  const assessmentInfo = useAppSelector((s) => selectAssessmentInfoForPage(s, page.id));
  const assessmentStatus = assessmentInfo?.status;
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, page.id));
  const { canAuthorCourse, canParticipateInCourse } = useCourseCapabilitiesByCourseId(
    page.course_id,
  );
  const courseUser = useAppSelector((s) => selectCourseUserByCourseId(s, page.course_id));
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForCourseUser(s, page.id, courseUser?.id),
  );

  let assessmentChip: ReactNode = null;
  if (
    (canAuthorCourse && assessmentStatus === AssessmentStatus.ASSESSABLE) ||
    assessmentStatus === AssessmentStatus.ASSESSED
  ) {
    assessmentChip = (
      <>
        {canParticipateInCourse &&
          assessmentStatus === AssessmentStatus.ASSESSED &&
          assessments.some((a) => a.comment) && (
            <Tooltip text={t("assessment.instructor_comment")}>
              <ChatIcon />
            </Tooltip>
          )}
        <AssessmentStatusChip
          assessedSubmissionsCount={assessmentInfo?.assessed_submissions_count}
          assessmentStatus={assessmentStatus}
          page={page}
          unassessedLateCount={assessmentInfo?.unassessed_late_count}
        />
      </>
    );

    if (assessmentStatus === AssessmentStatus.ASSESSED || !alwaysShowDueDates) {
      return assessmentChip;
    }
  }

  if (canParticipateInCourse && pageAccessData?.completed_at) {
    return (
      <div className="list-item-chip cursor-default rounded-md border border-white bg-green-tint-90 px-2 py-1 text-xs font-medium text-green-shade-20">
        <span>{t("common.completed")}</span>
      </div>
    );
  }

  if (page.due_at) {
    return (
      <>
        {assessmentChip}
        <DueDateButton
          kind="list-item"
          page={page}
          readOnly={readOnly}
          clickStopPropagation
          shortText
        />
      </>
    );
  }

  return null;
};
